import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axiosInstant from "../api";
import { getBrandProject, getContentTypes, getBrand, getBrandSOWs } from "../api/endpoints";
import {
    OrderProjectValues,
    previewPricing,
    PreviewProjectEstimate,
} from "../pages/orders/helper";
import { OrderPricingBreakdownData } from "../components/Pricing/OrderPricingBreakdown";

interface Brand {
    id: number;
    rmn?: boolean;
    [key: string]: any;
}

interface ContentTypesResult {
    contentTypes: any[];
    loading: boolean;
    refetch: () => void;
}

interface ChannelsResult {
    channels: any[];
    loading: boolean;
    refetch: () => void;
}

interface RmnBrandsResult {
    rmnBrandOptions: any[];
    loading: boolean;
    refetch: () => void;
}

export const useContentTypes = (selectedBrand: Brand | null): ContentTypesResult => {
    const [contentTypes, setContentTypes] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const isRmnBrand = useMemo(() => {
        return selectedBrand?.rmn && selectedBrand?.id;
    }, [selectedBrand?.rmn, selectedBrand?.id]);

    const fetchContentTypes = useCallback(async () => {
        setLoading(true);
        try {
            const url = isRmnBrand
                ? `/brands/${selectedBrand!.id}/content-types?isRmnBrand=true&expertConnect=true`
                : getContentTypes(true);
            const res = await axiosInstant.get(url);
            setContentTypes(res.data || []);
        } catch {
            setContentTypes([]);
        } finally {
            setLoading(false);
        }
    }, [isRmnBrand, selectedBrand?.id]);

    useEffect(() => {
        fetchContentTypes();
    }, [fetchContentTypes]);

    return { contentTypes, loading, refetch: fetchContentTypes };
};

export const useChannels = (
    selectedBrand: Brand | null,
    selectedContentTypeId: number | null,
    contentTypes: any[] | null
): ChannelsResult => {
    const [channels, setChannels] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const fetchChannels = useCallback(() => {
        if (!selectedContentTypeId || !contentTypes) {
            setChannels([]);
            return;
        }
        setLoading(true);
        // Use channels embedded in the already-loaded content type object.
        // With the generic 6-channel model every content type carries all
        // active channels (the junction only exists for future restriction);
        // there is no rmn distinction anymore.
        const contentType = contentTypes.find((ct: any) => ct.id === selectedContentTypeId);
        setChannels(contentType?.content_channels || []);
        setLoading(false);
    }, [selectedContentTypeId, contentTypes]);

    useEffect(() => {
        fetchChannels();
    }, [fetchChannels]);

    return { channels, loading, refetch: fetchChannels };
};

export const useRmnBrands = (selectedBrand: Brand | null): RmnBrandsResult => {
    const [rmnBrandOptions, setRmnBrandOptions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    const shouldFetch = useMemo(() => {
        return selectedBrand?.rmn && selectedBrand?.id;
    }, [selectedBrand?.rmn, selectedBrand?.id]);

    const fetchRmnBrands = useCallback(async () => {
        if (!shouldFetch) {
            setRmnBrandOptions([]);
            return;
        }
        setLoading(true);
        try {
            const res = await axiosInstant.get(getBrand(selectedBrand!.id));
            setRmnBrandOptions(res.data?.associated_brands || []);
        } catch {
            setRmnBrandOptions([]);
        } finally {
            setLoading(false);
        }
    }, [shouldFetch, selectedBrand?.id]);

    useEffect(() => {
        fetchRmnBrands();
    }, [fetchRmnBrands]);

    return { rmnBrandOptions, loading, refetch: fetchRmnBrands };
};

export const useEnrichedProjects = (
    mode: "create" | "edit",
    initialProjects: OrderProjectValues[] | undefined
): OrderProjectValues[] => {
    const [projects, setProjects] = useState<OrderProjectValues[]>(initialProjects || []);

    const shouldEnrich = useMemo(() => {
        return mode === "edit" && initialProjects?.length;
    }, [mode, initialProjects?.length]);

    const enrichProjects = useCallback(async () => {
        if (!shouldEnrich || !initialProjects?.length) {
            setProjects(initialProjects || []);
            return;
        }

        try {
            const enriched = await Promise.all(
                initialProjects.map(async (p) => {
                    if (!p.project_id) return p;
                    try {
                        const res = await axiosInstant.get(getBrandProject(p.project_id));
                        const proj = res.data;
                        const projectBrands = proj.brands || [];
                        const nonRmnBrand = projectBrands.find((b: any) => !b.rmn);

                        const channelObjects = proj.content_channels || [];

                        return {
                            ...p,
                            status: proj.status ?? p.status ?? null,
                            project_name: proj.title || p.project_name,
                            content_type: proj.content_type?.id ? String(proj.content_type.id) : "",
                            content_type_id: proj.content_type?.id ?? null,
                            channel: "",
                            channel_id: null,
                            channel_ids: channelObjects.map((ch: any) => ch.id),
                            channel_objects: channelObjects,
                            channel_name: channelObjects.map((ch: any) => ch.name).join(", ") || "",
                            rmn_brand_id: nonRmnBrand?.id ?? null,
                            product_name: proj.product?.title || "",
                            product_id: proj.product?.desireid ?? null,
                            product_ids: (proj.products || []).map((pd: any) => pd.desireid).filter(Boolean),
                            products: (proj.products || [])
                                .filter((pd: any) => pd.desireid)
                                .map((pd: any) => ({
                                    desireid: pd.desireid,
                                    id: pd.desireid,
                                    title: pd.title || "",
                                    image: pd.image
                                })),
                            start_date: proj.start_date ? new Date(proj.start_date) : null,
                            production_adjustment:
                                proj.production_adjustment ?? p.production_adjustment ?? null,
                            price_locked: proj.price_locked ?? p.price_locked ?? false
                        };
                    } catch {
                        return p;
                    }
                })
            );
            setProjects(enriched);
        } catch {
            setProjects(initialProjects);
        }
    }, [shouldEnrich, initialProjects]);

    useEffect(() => {
        enrichProjects();
    }, [enrichProjects]);

    return projects;
};

export interface PricingPreviewState {
    order: { total: number; breakdown: OrderPricingBreakdownData };
    byKey: Map<number, PreviewProjectEstimate>;
}

/**
 * Debounced live pricing preview for an unsaved draft order. Recomputes via the
 * backend preview endpoint whenever the pricing-relevant fields of `projects`
 * change. Skipped when `enabled` is false (e.g. brand users) or there are no
 * projects. Last-write-wins so a fast edit can't be overwritten by a slow
 * earlier response.
 */
export const usePricingPreview = (
    projects: OrderProjectValues[],
    brandId: number | undefined,
    enabled: boolean,
    sowId?: number | null,
): { pricingPreview: PricingPreviewState | null; loading: boolean } => {
    const [pricingPreview, setPricingPreview] =
        useState<PricingPreviewState | null>(null);
    const [loading, setLoading] = useState(false);
    const reqIdRef = useRef(0);

    // Signature over only the pricing-relevant fields so unrelated updates
    // (project name, product image enrichment) don't trigger a request.
    const signature = useMemo(
        () =>
            JSON.stringify(
                projects.map((p) => ({
                    c: p.content_type_id ?? null,
                    pr: (p.product_ids?.length
                        ? p.product_ids
                        : p.product_id
                          ? [p.product_id]
                          : []
                    )
                        .slice()
                        .sort((a, b) => a - b),
                    ch: (p.channel_ids?.length
                        ? p.channel_ids
                        : p.channel_id
                          ? [p.channel_id]
                          : []
                    )
                        .slice()
                        .sort((a, b) => a - b),
                    adj: p.production_adjustment ?? null,
                })),
            ),
        [projects],
    );

    useEffect(() => {
        if (!enabled || projects.length === 0) {
            // Invalidate any in-flight request too — without the bump, a
            // response for the previous project set lands with a matching
            // reqId and resurrects a stale total for an empty order.
            reqIdRef.current += 1;
            setPricingPreview(null);
            setLoading(false);
            return;
        }
        const handle = setTimeout(async () => {
            const myReq = ++reqIdRef.current;
            setLoading(true);
            try {
                const data = await previewPricing(projects, brandId, sowId);
                if (myReq !== reqIdRef.current) return; // superseded by a newer edit
                const byKey = new Map<number, PreviewProjectEstimate>();
                for (const est of data.per_project_estimates) {
                    byKey.set(est.key, est);
                }
                setPricingPreview({ order: data.order, byKey });
            } catch {
                if (myReq === reqIdRef.current) setPricingPreview(null);
            } finally {
                if (myReq === reqIdRef.current) setLoading(false);
            }
        }, 600);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [signature, enabled, brandId, sowId]);

    return { pricingPreview, loading };
};

export interface SOWItem {
    id: number;
    name: string;
    url: string;
    brand?: { id: number; name: string };
    // Package engagement fields — present when the SOW was sold as a package.
    package_id?: number | null;
    package_snapshot?: {
        display_name: string;
        sell_price: string | number;
        max_skus: number;
        term_months: number | null;
        payment_schedule?: string | null;
    } | null;
    start_date?: string | null;
    end_date?: string | null;
    package_usage?: { skus_used: number; project_count: number; order_count: number };
}

interface SOWsResult {
    sows: SOWItem[];
    loading: boolean;
    refetch: () => void;
}

export const useSOWsByBrand = (selectedBrand: Brand | null): SOWsResult => {
    const [sows, setSOWs] = useState<SOWItem[]>([]);
    const [loading, setLoading] = useState(false);

    const brandId = useMemo(() => selectedBrand?.id || null, [selectedBrand?.id]);

    const fetchSOWs = useCallback(async () => {
        if (!brandId) {
            setSOWs([]);
            return;
        }

        setLoading(true);
        try {
            const res = await axiosInstant.get(getBrandSOWs());
            const allSOWs: SOWItem[] = res.data?.rows || res.data || [];
            const filteredSOWs = allSOWs.filter((sow) => sow.brand?.id === brandId);
            setSOWs(filteredSOWs);
        } catch {
            setSOWs([]);
        } finally {
            setLoading(false);
        }
    }, [brandId]);

    useEffect(() => {
        fetchSOWs();
    }, [fetchSOWs]);

    return { sows, loading, refetch: fetchSOWs };
};
